export type CookieProvider = Pick<Document, "cookie">;
export type LocalStorageProvider = Pick<Window["localStorage"], "getItem" | "setItem" | "removeItem">;

export type BrowserServiceDependencies = {
	cookieProvider: CookieProvider;
	localStorageProvider: LocalStorageProvider;
	refreshHandler: VoidFunction;
};

/**
 * Mediates access to the browser in the service layer.
 */
export class BrowserService {
	static readonly EXPIRE_COOKIE_DATE = "Thu, 01 Jan 1970 00:00:01 GMT";

	readonly #cookieProvider: CookieProvider;
	readonly #localStorageProvider: LocalStorageProvider;
	readonly #refreshHandler: VoidFunction;

	#cookieDomain: string | null = null;
	#secureCookie: boolean;

	constructor(deps: BrowserServiceDependencies) {
		this.#cookieProvider = deps.cookieProvider;
		this.#localStorageProvider = deps.localStorageProvider;
		this.#refreshHandler = deps.refreshHandler;

		if(process.env["NODE_ENV"] === "development") {
			this.#secureCookie = false;
		} else {
			this.#secureCookie = true;
		}
	}

	/**
	 * Called by `Services`.
	 */
	private initialise(cookieDomain: string): void {
		this.#cookieDomain = cookieDomain;
	}

	getCookie(name: string): string | null {
		const cookie = this.#cookieProvider.cookie;
		return cookie
			.split(";")
			.map(cookie => cookie.trim().split("="))
			.find(([ key  ]) => key === name)
			?.[1] ?? null;
	}

	hasCookie(name: string): boolean {
		return !!this.getCookie(name);
	}

	#getCookieDomain(): string | null {
		if(!this.#cookieDomain) {
			throw new Error("BrowserService was not initialised.");
		}

		if(process.env["NODE_ENV"] === "development") {
			return "localhost";
		}

		return this.#cookieDomain;
	}

	#makeCookieAttributes(maxAge?: number | undefined): string {
		let attr = `SameSite=Strict; Domain=${this.#getCookieDomain()}; Path=/;}`;
		if(maxAge) {
			attr += `Max-Age=${maxAge};`;
		}
		if(this.#secureCookie) {
			attr += " Secure=true;";
		}

		return attr;
	}

	setCookie(name: string, value: string, maxAge?: number): void {
		this.#cookieProvider.cookie = `${name}=${value}; ${this.#makeCookieAttributes(maxAge)}`;
	}

	deleteCookie(name: string): void {
		this.#cookieProvider.cookie = `${name}=; expires=${BrowserService.EXPIRE_COOKIE_DATE}; ${this.#makeCookieAttributes()}`;
	}

	setLocalStorage(key: string, value: string): void {
		this.#localStorageProvider.setItem(key, value);
	}

	getLocalStorage(key: string): string | null {
		return this.#localStorageProvider.getItem(key);
	}

	deleteLocalStorage(key: string): void {
		this.#localStorageProvider.removeItem(key);
	}

	refresh(): void {
		this.#refreshHandler();
	}
}
